import type { AppState } from "./types";
import type { RootState } from "./redux";
import { PublicEndpoints } from "./constants";
import { getAppInfo, getTokenData } from "./functions";
// @ts-ignore
import Cookies from "js-cookie";

import { registerAction, authenticateAction } from "./components/ImageCapture/ImageCaptureSlice";
import {
  checkStatusByIsraeliIdAction,
  checkStatusByRegNumAction,
  validateUserIsraeliIdAction,
  validateUserRegNumAction,
} from "./components/RegistrationPage/RegistrationPageSlice";
import {
  getUsersAction,
  loginAdminAction,
  searchUserByRegNumAction,
  resetCountAction,
  logoutAdminAction,
  getSingleUserAction,
  getUserImagesAction,
  getTotalUsersAction,
  changePasswordAction,
  loginReturningAdminAction,
  deleteAttempsAction,
  getAllRolesAction,
  addNewUserAction,
  switchUserStatus,
  searchUserByIsraeliIdAction,
} from "./components/Dashboard/DashboardSlice";
import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { confirmContactInfoAction, getContactInfoAction } from "./components/CompletionPage/ContactInfoSlice";
import { contries } from "./utility/contriesLidt";
import {
  cleanUnderReviewAction,
  getCaseForReviewAction,
  getClaimsTypeAction,
  submitDecisionAction,
} from "./components/Queue/QueueSlice";

const initialState: AppState = {
  error: null,
  success: null,
  isLoading: false,
  requestId: null,
  requestSource: "paneem",
  isValidated: false,
  isAuthentication: false,
  isLoggedIn: false,
  version: "",
  underConstruction: false,
  releaseDate: "",
  contries: contries,
  isCompleted: false,
  paneemStatus: null,
  isPaneemTypeSelected: false,
};

export const getTokenDataAction = createAsyncThunk(PublicEndpoints.portal, async ({ token }: { token: string }) => {
  const response = await getTokenData(token);
  return response.data;
});

export const getAppInfoAction = createAsyncThunk(PublicEndpoints.info, async () => {
  await getAppInfo();
});

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setError: (state, { payload }: { payload: string | null }) => {
      state.error = payload;
    },

    setSuccess: (state, { payload }: { payload: string | null }) => {
      state.success = payload;
    },

    setIsLoading: (state, { payload }: { payload: boolean }) => {
      state.isLoading = payload;
    },

    setIsValidated: (state, { payload }: { payload: boolean }) => {
      state.isValidated = payload;
    },

    setIsAuthentication: (state, { payload }: { payload: boolean }) => {
      state.isAuthentication = payload;
    },
    setUnderConstruction: (state) => {
      state.underConstruction = false;
    },

    setIsPaneemTypeSelected: (state) => {
      state.isPaneemTypeSelected = true;
    },

    resetAppState: (state) => {
      return { ...initialState, releaseDate: state.releaseDate, version: state.version };
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getAppInfoAction.fulfilled, (state) => {
      state.version = Cookies.get("version") as string;
      state.releaseDate = Cookies.get("releaseDate") as string;
      state.underConstruction = Cookies.get("underConstruction")
        ? (JSON.parse(Cookies.get("underConstruction") as "false" | "true") as boolean)
        : false;
    });
    builder.addCase(getContactInfoAction.fulfilled, (state, { payload }) => {
      state.isLoading = false;
    });

    builder.addCase(addNewUserAction.fulfilled, (state, { payload }) => {
      if (!payload.success) {
        state.error = payload.msg;
      }
      state.success = payload.msg;
    });
    builder.addCase(deleteAttempsAction.fulfilled, (state, { payload }) => {
      state.success = payload.msg;
    });
    builder.addCase(getAllRolesAction.fulfilled, (state, { payload }) => {
      state.success = payload.message;
    });
    builder.addCase(getContactInfoAction.rejected, (state, { payload }) => {
      state.isLoading = false;
      state.paneemStatus = null;
    });
    builder.addCase(getTokenDataAction.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.requestId = payload.requestId;
      state.requestSource = payload.requestSource;
      state.isValidated = true;
      state.isAuthentication = payload.isAuthenticated;
      if (state.underConstruction) {
        state.underConstruction = payload.requestId ? false : true;
      }
    });

    builder.addCase(registerAction.fulfilled, (state, { payload }) => {
      state.isLoading = false;

      if (payload!.message === "document_back") return;
      else {
        state.paneemStatus = payload!.status as string;
        state.isCompleted = true;
      }
    });

    builder.addCase(authenticateAction.fulfilled, (state, { payload }) => {
      state.paneemStatus = payload!.status as string;
      state.isLoading = false;
      state.isCompleted = true;
    });

    builder.addCase(getUsersAction.rejected, (state, { payload }) => {
      state.isLoading = false;

      switch (payload) {
        case 401: {
          state.isLoggedIn = false;
          state.error = "Session timeout.";

          break;
        }

        case 400: {
          state.error = "Parameter must be a number.";

          break;
        }

        case 500: {
          state.error = "An unknown error has ocurred.";

          break;
        }

        default:
          state.error = payload as string;
      }
    });

    builder.addCase(getUserImagesAction.fulfilled, (state, { payload }) => {
      state.isLoading = false;

      state.isAuthentication = payload!.isAuthentication;
    });

    builder.addCase(getUserImagesAction.rejected, (state, { payload }) => {
      state.isLoading = false;

      switch (payload) {
        case 401: {
          state.isLoggedIn = false;
          state.error = "Session timeout.";

          break;
        }

        case 400: {
          state.error = "Invalid ID.";

          break;
        }

        case 404: {
          state.error = "Images not found.";

          break;
        }

        case 500: {
          state.error = "An unknown error has ocurred.";

          break;
        }

        default:
          state.error = payload as string;
      }
    });

    builder.addCase(resetCountAction.fulfilled, (state) => {
      state.isLoading = false;
      state.success = "Claimant count number reset.";
    });

    builder.addCase(resetCountAction.rejected, (state, { payload }) => {
      state.isLoading = false;

      switch (payload) {
        case 401: {
          state.isLoggedIn = false;
          state.error = "Session timeout.";

          break;
        }

        case 400: {
          state.error = "Invalid registration number.";

          break;
        }

        case 403: {
          state.error = "Claimant is already approved.";

          break;
        }

        case 404: {
          state.error = "Registration number not found.";

          break;
        }

        case 500: {
          state.error = "An unknown error has ocurred.";

          break;
        }

        default:
          state.error = payload as string;
      }
    });

    builder.addCase(changePasswordAction.fulfilled, (state) => {
      state.isLoading = false;
      state.success = "Password change successfully.";
    });
    builder.addCase(getCaseForReviewAction.fulfilled, (state, { payload }) => {
      if (!payload.attempts.length) {
        state.success = "All Cases Reviewed";
      }
    });

    builder.addCase(switchUserStatus.fulfilled, (state, { payload }) => {
      if (payload.updated) {
        state.success = "Switched Status Successfully";
      }
    });
    builder.addCase(switchUserStatus.rejected, (state, { payload }) => {
      state.error = "Failed to switch status - an error accured";
    });

    builder.addCase(changePasswordAction.rejected, (state, { payload }) => {
      state.isLoading = false;

      switch (payload) {
        case 401: {
          state.isLoggedIn = false;
          state.error = "Session timeout.";

          break;
        }

        case 400: {
          state.error = "Password incorrect.";

          break;
        }

        case 500: {
          state.error = "An unknown error has ocurred.";

          break;
        }

        default:
          state.error = payload as string;
      }
    });

    // builder.addCase(validateUserRegNumAction.fulfilled, (state, { payload }) => {
    //   state.isLoading = false;
    //   state.isValidated = payload!.isValidated;
    //   state.isAuthentication = payload!.isAuthentication;
    // });

    builder.addMatcher(
      isAnyOf(validateUserRegNumAction.fulfilled, validateUserIsraeliIdAction.fulfilled),
      (state, { payload }) => {
        state.isLoading = false;
        state.isValidated = payload!.isValidated;
        state.isAuthentication = payload!.isAuthentication;
      }
    );
    builder.addMatcher(
      isAnyOf(checkStatusByRegNumAction.fulfilled, checkStatusByIsraeliIdAction.fulfilled),
      (state, { payload }) => {
        state.isLoading = false;
        state.success = payload!.status;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getTokenDataAction.rejected,
        loginReturningAdminAction.rejected,
        searchUserByRegNumAction.fulfilled,
        searchUserByIsraeliIdAction.fulfilled,
        registerAction.rejected,
        authenticateAction.rejected,
        getUsersAction.fulfilled,
        getTotalUsersAction.fulfilled,
        getSingleUserAction.fulfilled,
        getAppInfoAction.fulfilled,
        confirmContactInfoAction.rejected,
        deleteAttempsAction.rejected,
        getAllRolesAction.rejected,
        addNewUserAction.rejected,
        getClaimsTypeAction.fulfilled,
        getCaseForReviewAction.rejected,
        getCaseForReviewAction.rejected,
        getCaseForReviewAction.fulfilled,
        cleanUnderReviewAction.fulfilled
      ),
      (state) => {
        state.isLoading = false;
      }
    );

    builder.addMatcher(
      isAnyOf(
        checkStatusByRegNumAction.rejected,
        checkStatusByIsraeliIdAction.rejected,
        validateUserRegNumAction.rejected,
        validateUserIsraeliIdAction.rejected,
        loginAdminAction.rejected,
        deleteAttempsAction.rejected,
        getAllRolesAction.rejected,
        addNewUserAction.rejected,
        getClaimsTypeAction.rejected,
        getCaseForReviewAction.rejected,
        getCaseForReviewAction.rejected
      ),
      (state, { payload }) => {
        state.isLoading = false;
        state.error = payload as string;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getTokenDataAction.pending,
        checkStatusByRegNumAction.pending,
        checkStatusByIsraeliIdAction.pending,
        validateUserRegNumAction.pending,
        validateUserIsraeliIdAction.pending,
        registerAction.pending,
        authenticateAction.pending,
        loginReturningAdminAction.pending,
        loginAdminAction.pending,
        logoutAdminAction.pending,
        getUsersAction.pending,
        getSingleUserAction.pending,
        getUserImagesAction.pending,
        searchUserByRegNumAction.pending,
        searchUserByIsraeliIdAction.pending,
        resetCountAction.pending,
        changePasswordAction.pending,
        getTotalUsersAction.pending,
        getAppInfoAction.pending,
        confirmContactInfoAction.pending,
        getCaseForReviewAction.pending,
        submitDecisionAction.pending,
        getClaimsTypeAction.pending,
        cleanUnderReviewAction.pending
      ),
      (state) => {
        state.isLoading = true;
      }
    );

    builder.addMatcher(isAnyOf(loginReturningAdminAction.fulfilled, loginAdminAction.fulfilled), (state) => {
      state.isLoading = false;
      state.isLoggedIn = true;
    });

    builder.addMatcher(
      isAnyOf(logoutAdminAction.fulfilled, confirmContactInfoAction.fulfilled, logoutAdminAction.rejected),
      (state) => {
        state.isLoading = false;
        state.isLoggedIn = false;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getSingleUserAction.rejected,
        searchUserByRegNumAction.rejected,
        searchUserByIsraeliIdAction.rejected,
        getTotalUsersAction.rejected
      ),
      (state, { payload }) => {
        state.isLoading = false;

        switch (payload) {
          case 401: {
            state.isLoggedIn = false;
            state.error = "Session timeout.";

            break;
          }

          case 400: {
            state.error = "Invalid registration number.";

            break;
          }

          case 404: {
            state.error = "Registration number not found.";

            break;
          }

          case 500: {
            state.error = "An unknown error has ocurred.";

            break;
          }

          default:
            state.error = payload as string;
        }
      }
    );
  },
});

export const {
  setIsLoading,
  setError,
  setSuccess,
  setIsValidated,
  setIsAuthentication,
  setUnderConstruction,
  setIsPaneemTypeSelected,
  resetAppState,
} = appSlice.actions;

export const selectError = (state: RootState) => state.app.error;
export const selectSuccess = (state: RootState) => state.app.success;
export const selectIsLoading = (state: RootState) => state.app.isLoading;
export const selectRequestId = (state: RootState) => state.app.requestId;
export const selectRequestSource = (state: RootState) => state.app.requestSource;
export const selectIsValidated = (state: RootState) => state.app.isValidated;
export const selectIsAuthentication = (state: RootState) => state.app.isAuthentication;
export const selectIsCompleted = (state: RootState) => state.app.isCompleted;
export const selectIsLoggedIn = (state: RootState) => state.app.isLoggedIn;
export const selectVersion = (state: RootState) => state.app.version;
export const selectReleaseDate = (state: RootState) => state.app.releaseDate;
export const selectUnderConstruction = (state: RootState) => state.app.underConstruction;
export const selectPaneemStatus = (state: RootState) => state.app.paneemStatus;
export const selectContries = (state: RootState) => state.app.contries;
export const selectIsPaneemTypeSelected = (state: RootState) => state.app.isPaneemTypeSelected;

export default appSlice.reducer;
